/**
 * AxLabs Mathematics Experiments - Functions
 *
 * Collection of experiments exploring function properties and behaviors.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// DOMAIN RANGE EXPLORER
// ============================================================================
export const DomainRangeExplorer = createExperimentFromSchema({
    id: 'domain-range-explorer',
    name: 'Domain Range Explorer',
    domain: 'mathematics',
    category: 'functions',
    description: 'Investigate domain and range of various function types',
    conceptTags: ['Domain', 'Range', 'Functions', 'Input/Output'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: -5, symbol: 'x' },
        { id: 'y', name: 'Output f(x)', unit: 'dimensionless', initial: -25, symbol: 'f(x)' },
        { id: 'validX', name: 'In Domain', unit: 'dimensionless', initial: 1, symbol: 'D' },
        { id: 'minY', name: 'Min Range', unit: 'dimensionless', initial: -25, symbol: 'y_{min}' },
        { id: 'maxY', name: 'Max Range', unit: 'dimensionless', initial: 25, symbol: 'y_{max}' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function Type', unit: 'dimensionless', min: 0, max: 5, default: 0, symbol: 'f' },
        { id: 'paramA', name: 'Parameter a', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'a' },
        { id: 'paramB', name: 'Parameter b', unit: 'dimensionless', min: -5, max: 5, default: 0, symbol: 'b' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'domain-def',
            name: 'Domain Definition',
            latex: 'D = \\{x \\in \\mathbb{R} : f(x) \\text{ is defined}\\}',
            description: 'All valid input values for the function',
        },
        {
            id: 'range-def',
            name: 'Range Definition',
            latex: 'R = \\{f(x) : x \\in D\\}',
            description: 'All possible output values of the function',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const a = getParam(params, 'paramA');
        const b = getParam(params, 'paramB');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let y = 0;
        let validX = 1;
        switch (funcType) {
            case 0: // Quadratic
                y = a * nextX * nextX + b;
                validX = 1;
                break;
            case 1: // Square root
                if (nextX >= 0) {
                    y = a * Math.sqrt(nextX) + b;
                    validX = 1;
                }
                else {
                    y = 0;
                    validX = 0;
                }
                break;
            case 2: // Rational
                if (Math.abs(nextX - a) > 0.001) {
                    y = b / (nextX - a);
                    validX = 1;
                }
                else {
                    y = 0;
                    validX = 0;
                }
                break;
            case 3: // Logarithmic
                if (nextX > 0) {
                    y = a * Math.log(nextX) + b;
                    validX = 1;
                }
                else {
                    y = 0;
                    validX = 0;
                }
                break;
            case 4: // Exponential
                y = a * Math.exp(nextX) + b;
                validX = 1;
                break;
            case 5: // Absolute value
                y = a * Math.abs(nextX) + b;
                validX = 1;
                break;
        }
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            validX: validX - getState(state, 'validX'),
            minY: funcType === 0 && a > 0 ? b : -25 - getState(state, 'minY'),
            maxY: funcType === 4 && a > 0 ? 100 : 25 - getState(state, 'maxY'),
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        y: { min: -100, max: 100 },
    },
});
// ============================================================================
// COMPOSITION OF FUNCTIONS
// ============================================================================
export const CompositionOfFunctions = createExperimentFromSchema({
    id: 'composition-of-functions',
    name: 'Composition of Functions',
    domain: 'mathematics',
    category: 'functions',
    description: 'Explore function composition (f ∘ g)(x) = f(g(x))',
    conceptTags: ['Function Composition', 'Composite Functions', 'Order of Operations'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'gx', name: 'g(x)', unit: 'dimensionless', initial: 0, symbol: 'g(x)' },
        { id: 'fx', name: 'f(x)', unit: 'dimensionless', initial: 0, symbol: 'f(x)' },
        { id: 'fogx', name: '(f∘g)(x)', unit: 'dimensionless', initial: 0, symbol: '(f \\circ g)(x)' },
        { id: 'gofx', name: '(g∘f)(x)', unit: 'dimensionless', initial: 0, symbol: '(g \\circ f)(x)' },
    ],
    parameters: [
        { id: 'fType', name: 'f(x) Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'f' },
        { id: 'gType', name: 'g(x) Type', unit: 'dimensionless', min: 0, max: 3, default: 1, symbol: 'g' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'composition-def',
            name: 'Function Composition',
            latex: '(f \\circ g)(x) = f(g(x))',
            description: 'Apply g first, then f to the result',
        },
        {
            id: 'non-commutative',
            name: 'Non-commutativity',
            latex: '(f \\circ g)(x) \\neq (g \\circ f)(x)',
            description: 'Composition is generally not commutative',
        },
    ],
    derivatives: (state, params) => {
        const fType = Math.round(getParam(params, 'fType'));
        const gType = Math.round(getParam(params, 'gType'));
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        // Define functions
        const applyFunc = (type, val) => {
            switch (type) {
                case 0: return val + 1; // f(x) = x + 1
                case 1: return 2 * val; // f(x) = 2x
                case 2: return val * val; // f(x) = x²
                case 3: return Math.sqrt(Math.max(0, val)); // f(x) = √x
                default: return val;
            }
        };
        const gx = applyFunc(gType, nextX);
        const fx = applyFunc(fType, nextX);
        const fogx = applyFunc(fType, gx);
        const gofx = applyFunc(gType, fx);
        return {
            x: scanRate * 0.016,
            gx: gx - getState(state, 'gx'),
            fx: fx - getState(state, 'fx'),
            fogx: fogx - getState(state, 'fogx'),
            gofx: gofx - getState(state, 'gofx'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        gx: { min: -10, max: 50 },
        fx: { min: -10, max: 50 },
    },
});
// ============================================================================
// INVERSE FUNCTION SYMMETRY
// ============================================================================
export const InverseFunctionSymmetry = createExperimentFromSchema({
    id: 'inverse-function-symmetry',
    name: 'Inverse Function Symmetry',
    domain: 'mathematics',
    category: 'functions',
    description: 'Explore the relationship between functions and their inverses',
    conceptTags: ['Inverse Functions', 'Symmetry', 'One-to-One', 'Reflection'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 2, symbol: 'f(x)' },
        { id: 'invX', name: 'f⁻¹(x)', unit: 'dimensionless', initial: 1, symbol: 'f^{-1}(x)' },
        { id: 'composed', name: 'f⁻¹(f(x))', unit: 'dimensionless', initial: 1, symbol: 'f^{-1}(f(x))' },
        { id: 'reflectedY', name: 'Reflected Point y', unit: 'dimensionless', initial: 1, symbol: 'y_{ref}' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function Type', unit: 'dimensionless', min: 0, max: 4, default: 0, symbol: 'f' },
        { id: 'paramA', name: 'Parameter a', unit: 'dimensionless', min: 0.5, max: 3, default: 2, symbol: 'a' },
        { id: 'paramB', name: 'Parameter b', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'b' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.02, symbol: 'v' },
    ],
    equations: [
        {
            id: 'inverse-def',
            name: 'Inverse Function Definition',
            latex: 'f^{-1}(f(x)) = x',
            description: 'The inverse "undoes" the original function',
        },
        {
            id: 'reflection',
            name: 'Line y = x Reflection',
            latex: '\\text{Graph of } f^{-1} \\text{ is reflection of } f \\text{ across } y=x',
            description: 'Inverse functions are symmetric about y = x',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const a = getParam(params, 'paramA');
        const b = getParam(params, 'paramB');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let y = 0;
        let invX = 0;
        let composed = 0;
        switch (funcType) {
            case 0: // Linear: f(x) = ax + b
                y = a * nextX + b;
                invX = (nextX - b) / a;
                composed = (a * nextX + b - b) / a;
                break;
            case 1: // Exponential: f(x) = b * a^x
                y = b * Math.pow(a, nextX);
                invX = Math.log(Math.max(0.001, nextX / b)) / Math.log(a);
                composed = Math.log(b * Math.pow(a, nextX) / b) / Math.log(a);
                break;
            case 2: // Power: f(x) = x^a (for x >= 0)
                y = Math.pow(Math.max(0, nextX), a);
                invX = Math.pow(Math.max(0, nextX), 1 / a);
                composed = Math.pow(Math.pow(Math.max(0, nextX), a), 1 / a);
                break;
            case 3: // Logarithmic: f(x) = a * ln(x) + b
                y = a * Math.log(Math.max(0.001, nextX)) + b;
                invX = Math.exp((nextX - b) / a);
                composed = Math.exp((a * Math.log(Math.max(0.001, nextX)) + b - b) / a);
                break;
            case 4: // f(x) = a/(x+b)
                const denom = nextX + b;
                if (Math.abs(denom) > 0.001) {
                    y = a / denom;
                    invX = a / nextX - b;
                    composed = a / (a / nextX - b + b);
                }
                break;
        }
        const reflectedY = nextX;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            invX: invX - getState(state, 'invX'),
            composed: composed - getState(state, 'composed'),
            reflectedY: reflectedY - getState(state, 'reflectedY'),
        };
    },
    bounds: {
        x: { min: 0.1, max: 10 },
        y: { min: -10, max: 50 },
    },
});
// ============================================================================
// PIECEWISE FUNCTION CONTINUITY
// ============================================================================
export const PiecewiseFunctionContinuity = createExperimentFromSchema({
    id: 'piecewise-function-continuity',
    name: 'Piecewise Function Continuity',
    domain: 'mathematics',
    category: 'functions',
    description: 'Investigate continuity at piecewise function boundaries',
    conceptTags: ['Piecewise Functions', 'Continuity', 'Limits', 'Piecewise Definitions'],
    difficulty: 'advanced',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: -3, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: -2, symbol: 'f(x)' },
        { id: 'leftLimit', name: 'lim x→c⁻ f(x)', unit: 'dimensionless', initial: 1, symbol: '\\lim_{x\\to c^-}' },
        { id: 'rightLimit', name: 'lim x→c⁺ f(x)', unit: 'dimensionless', initial: 1, symbol: '\\lim_{x\\to c^+}' },
        { id: 'isContinuous', name: 'Continuous at c', unit: 'dimensionless', initial: 1, symbol: 'C' },
        { id: 'jump', name: 'Jump Discontinuity', unit: 'dimensionless', initial: 0, symbol: 'J' },
    ],
    parameters: [
        { id: 'breakpoint', name: 'Breakpoint c', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'c' },
        { id: 'leftCoeff', name: 'Left Slope', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'm_L' },
        { id: 'rightCoeff', name: 'Right Slope', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'm_R' },
        { id: 'leftIntercept', name: 'Left Intercept', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'b_L' },
        { id: 'rightIntercept', name: 'Right Intercept', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'b_R' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.03, symbol: 'v' },
    ],
    equations: [
        {
            id: 'continuity-def',
            name: 'Continuity Definition',
            latex: '\\lim_{x\\to c^-} f(x) = \\lim_{x\\to c^+} f(x) = f(c)',
            description: 'Function is continuous at c if left limit, right limit, and function value agree',
        },
        {
            id: 'jump-discontinuity',
            name: 'Jump Discontinuity',
            latex: 'J = |\\lim_{x\\to c^+} f(x) - \\lim_{x\\to c^-} f(x)|',
            description: 'Size of the gap in the function',
        },
    ],
    derivatives: (state, params) => {
        const c = getParam(params, 'breakpoint');
        const mL = getParam(params, 'leftCoeff');
        const mR = getParam(params, 'rightCoeff');
        const bL = getParam(params, 'leftIntercept');
        const bR = getParam(params, 'rightIntercept');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let y = 0;
        if (nextX < c) {
            y = mL * nextX + bL;
        }
        else {
            y = mR * nextX + bR;
        }
        const leftLimit = mL * c + bL;
        const rightLimit = mR * c + bR;
        const jump = Math.abs(rightLimit - leftLimit);
        const isContinuous = jump < 0.001 ? 1 : 0;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            leftLimit: leftLimit - getState(state, 'leftLimit'),
            rightLimit: rightLimit - getState(state, 'rightLimit'),
            isContinuous: isContinuous - getState(state, 'isContinuous'),
            jump: jump - getState(state, 'jump'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        y: { min: -20, max: 20 },
    },
});
// ============================================================================
// EVEN/ODD FUNCTION SYMMETRY
// ============================================================================
export const EvenOddFunctionSymmetry = createExperimentFromSchema({
    id: 'even-odd-function-symmetry',
    name: 'Even/Odd Function Symmetry',
    domain: 'mathematics',
    category: 'functions',
    description: 'Explore even and odd function properties and symmetry',
    conceptTags: ['Even Functions', 'Odd Functions', 'Symmetry', 'Function Properties'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: -3, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 9, symbol: 'f(x)' },
        { id: 'negX', name: 'f(-x)', unit: 'dimensionless', initial: 9, symbol: 'f(-x)' },
        { id: 'isEven', name: 'Is Even', unit: 'dimensionless', initial: 1, symbol: 'E' },
        { id: 'isOdd', name: 'Is Odd', unit: 'dimensionless', initial: 0, symbol: 'O' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function Type', unit: 'dimensionless', min: 0, max: 5, default: 0, symbol: 'f' },
        { id: 'paramA', name: 'Coefficient a', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'a' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.03, symbol: 'v' },
    ],
    equations: [
        {
            id: 'even-def',
            name: 'Even Function',
            latex: 'f(-x) = f(x) \\quad \\text{(y-axis symmetry)}',
            description: 'Even functions are symmetric about the y-axis',
        },
        {
            id: 'odd-def',
            name: 'Odd Function',
            latex: 'f(-x) = -f(x) \\quad \\text{(origin symmetry)}',
            description: 'Odd functions are symmetric about the origin',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const a = getParam(params, 'paramA');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let y = 0;
        let yNeg = 0;
        switch (funcType) {
            case 0: // x² - even
                y = a * nextX * nextX;
                yNeg = a * (-nextX) * (-nextX);
                break;
            case 1: // x³ - odd
                y = a * nextX * nextX * nextX;
                yNeg = a * (-nextX) * (-nextX) * (-nextX);
                break;
            case 2: // cos(x) - even
                y = a * Math.cos(nextX);
                yNeg = a * Math.cos(-nextX);
                break;
            case 3: // sin(x) - odd
                y = a * Math.sin(nextX);
                yNeg = a * Math.sin(-nextX);
                break;
            case 4: // |x| - even
                y = a * Math.abs(nextX);
                yNeg = a * Math.abs(-nextX);
                break;
            case 5: // Neither
                y = a * (nextX * nextX + nextX);
                yNeg = a * ((-nextX) * (-nextX) + (-nextX));
                break;
        }
        const tolerance = 0.001;
        const isEven = Math.abs(y - yNeg) < tolerance ? 1 : 0;
        const isOdd = Math.abs(y + yNeg) < tolerance ? 1 : 0;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            negX: yNeg - getState(state, 'negX'),
            isEven: isEven - getState(state, 'isEven'),
            isOdd: isOdd - getState(state, 'isOdd'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        y: { min: -50, max: 50 },
    },
});
// ============================================================================
// FUNCTION TRANSLATION EFFECTS
// ============================================================================
export const FunctionTranslationEffects = createExperimentFromSchema({
    id: 'function-translation-effects',
    name: 'Function Translation Effects',
    domain: 'mathematics',
    category: 'functions',
    description: 'Explore how translations transform function graphs',
    conceptTags: ['Transformations', 'Translations', 'Function Shifts', 'Graph Transformations'],
    difficulty: 'beginner',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'yBase', name: 'Base f(x)', unit: 'dimensionless', initial: 0, symbol: 'f(x)' },
        { id: 'y', name: 'Transformed f(x)', unit: 'dimensionless', initial: 0, symbol: 'T(x)' },
        { id: 'shiftX', name: 'Horizontal Shift', unit: 'dimensionless', initial: 0, symbol: 'h' },
        { id: 'shiftY', name: 'Vertical Shift', unit: 'dimensionless', initial: 0, symbol: 'k' },
    ],
    parameters: [
        { id: 'funcType', name: 'Base Function', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'f' },
        { id: 'h', name: 'Horizontal Shift', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'h' },
        { id: 'k', name: 'Vertical Shift', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'k' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'translation-form',
            name: 'Translation Form',
            latex: 'T(x) = f(x-h) + k',
            description: 'Shift function h units horizontally, k units vertically',
        },
        {
            id: 'horizontal-shift',
            name: 'Horizontal Shift',
            latex: 'f(x-h) \\text{ shifts } \\begin{cases} \\text{right if } h>0 \\text{ left if } h<0 \\end{cases}',
            description: 'Opposite direction of sign',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const h = getParam(params, 'h');
        const k = getParam(params, 'k');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        let yBase = 0;
        const shiftedX = nextX - h;
        switch (funcType) {
            case 0: // Quadratic
                yBase = shiftedX * shiftedX;
                break;
            case 1: // Sine
                yBase = Math.sin(shiftedX);
                break;
            case 2: // Absolute value
                yBase = Math.abs(shiftedX);
                break;
            case 3: // Exponential
                yBase = Math.exp(shiftedX) - 1;
                break;
        }
        const y = yBase + k;
        return {
            x: scanRate * 0.016,
            yBase: yBase - getState(state, 'yBase'),
            y: y - getState(state, 'y'),
            shiftX: h - getState(state, 'shiftX'),
            shiftY: k - getState(state, 'shiftY'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        y: { min: -10, max: 20 },
    },
});
// ============================================================================
// PERIODIC FUNCTION BEHAVIOR
// ============================================================================
export const PeriodicFunctionBehavior = createExperimentFromSchema({
    id: 'periodic-function-behavior',
    name: 'Periodic Function Behavior',
    domain: 'mathematics',
    category: 'functions',
    description: 'Explore properties of periodic functions',
    conceptTags: ['Periodic Functions', 'Period', 'Frequency', 'Amplitude', 'Oscillation'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'y', name: 'f(t)', unit: 'dimensionless', initial: 0, symbol: 'f(t)' },
        { id: 'period', name: 'Period T', unit: 's', initial: 6.28, symbol: 'T' },
        { id: 'frequency', name: 'Frequency f', unit: 'Hz', initial: 0.16, symbol: 'f' },
        { id: 'amplitude', name: 'Amplitude A', unit: 'dimensionless', initial: 1, symbol: 'A' },
        { id: 'completed', name: 'Cycles Completed', unit: 'dimensionless', initial: 0, symbol: 'N' },
    ],
    parameters: [
        { id: 'funcType', name: 'Function Type', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'f' },
        { id: 'A', name: 'Amplitude', unit: 'dimensionless', min: 0.1, max: 5, default: 1, symbol: 'A' },
        { id: 'omega', name: 'Angular Frequency', unit: 'rad/s', min: 0.1, max: 5, default: 1, symbol: '\\omega' },
        { id: 'phi', name: 'Phase Shift', unit: 'rad', min: -Math.PI, max: Math.PI, default: 0, symbol: '\\phi' },
    ],
    equations: [
        {
            id: 'periodic-def',
            name: 'Periodic Function Definition',
            latex: 'f(t + T) = f(t) \\text{ for all } t',
            description: 'Function repeats after period T',
        },
        {
            id: 'frequency-period',
            name: 'Frequency-Period Relationship',
            latex: 'f = \\frac{1}{T}, \\quad \\omega = 2\\pi f = \\frac{2\\pi}{T}',
            description: 'Relationship between frequency, period, and angular frequency',
        },
    ],
    derivatives: (state, params) => {
        const funcType = Math.round(getParam(params, 'funcType'));
        const A = getParam(params, 'A');
        const omega = getParam(params, 'omega');
        const phi = getParam(params, 'phi');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        let y = 0;
        switch (funcType) {
            case 0: // Sine
                y = A * Math.sin(omega * nextT + phi);
                break;
            case 1: // Cosine
                y = A * Math.cos(omega * nextT + phi);
                break;
            case 2: // Square wave approximation
                y = A * Math.sign(Math.sin(omega * nextT + phi));
                break;
        }
        const period = (2 * Math.PI) / omega;
        const frequency = 1 / period;
        const completed = nextT / period;
        return {
            t: 0.016,
            y: y - getState(state, 'y'),
            period: period - getState(state, 'period'),
            frequency: frequency - getState(state, 'frequency'),
            amplitude: A - getState(state, 'amplitude'),
            completed: completed - getState(state, 'completed'),
        };
    },
    bounds: {
        t: { min: 0, max: 60 },
        y: { min: -10, max: 10 },
    },
});
// ============================================================================
// EXPORT ALL EXPERIMENTS
// ============================================================================
export const ALL_FUNCTIONS = [
    DomainRangeExplorer,
    CompositionOfFunctions,
    InverseFunctionSymmetry,
    PiecewiseFunctionContinuity,
    EvenOddFunctionSymmetry,
    FunctionTranslationEffects,
    PeriodicFunctionBehavior,
];
//# sourceMappingURL=functions.js.map